import React, { useState } from 'react';
import { ISession } from '../../interfaces/session.interface';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { getActiveBrand } from '../../store/selector/brand.selector';
import { useDeleteSessionMutation, useRenameSessionMutation } from '../../store/api/session.api';
import {
    RemoveSession,
    SetCurrentlyWorkingSession,
    UpdateSession,
} from '../../store/slice/session.slice';
import { getCurrentlyWorkingSession, getSessions } from '../../store/selector/session.selector';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { SetSelectedCampaign } from '../../store/slice/campaign.slice';

interface IProps {
    session: ISession;
    store: any;
}

const SidebarSession = ({ session, store }: IProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const isOnCampaignPage = location.pathname === '/campaign';
    const activeBrand = useAppSelector(getActiveBrand);
    const currentlyWorkingSession = useAppSelector(getCurrentlyWorkingSession);
    const sessions = useAppSelector(getSessions);
    const [updateSession] = useRenameSessionMutation();
    const [deleteSession] = useDeleteSessionMutation();

    const [isEditing, setIsEditing] = useState(false);
    const isCurrentlyWorking =
        isOnCampaignPage &&
        currentlyWorkingSession &&
        session.sessionKey === currentlyWorkingSession.sessionKey;

    const handleRenameSession = async (currentName: string) => {
        if (!activeBrand) return;
        setIsEditing(false);
        dispatch(UpdateSession({ ...session, name: currentName }));
        await updateSession({
            id: session.sessionKey,
            data: {
                name: currentName,
                brandId: activeBrand.id,
            },
        });
    };

    const handleDeleteSession = async () => {
        dispatch(RemoveSession(session.sessionKey));
        await deleteSession({
            id: session.sessionKey,
        });
    };

    const handleEditClick = () => {
        setIsEditing(true);
    };

    const handleSessionClick = () => {
        if (isCurrentlyWorking) return;
        store.clear();
        dispatch(SetSelectedCampaign(null));
        navigate(`/campaign?q=${session.sessionKey}`);
    };

    return (
        <div
            key={session.sessionKey}
            className={`tw-flex tw-items-center tw-rounded-md ${isCurrentlyWorking ? 'tw-bg-gray-300' : 'hover:tw-bg-gray-300'}`}
        >
            <div className="tw-w-full tw-px-2 tw-py-[9.5px]">
                {isEditing ? (
                    <input
                        ref={(input) => {
                            if (input) {
                                input.focus();
                            }
                        }}
                        style={{ border: '1px solid #6f4e84' }}
                        className="tw-w-full rounded tw-px-1"
                        type="text"
                        defaultValue={session.name}
                        onBlur={(e) => {
                            handleRenameSession(e.target.value);
                        }}
                        onKeyUp={(e: any) => {
                            if (e.key === 'Enter') {
                                handleRenameSession(e.target.value);
                            }
                        }}
                    />
                ) : (
                    <div
                        onClick={() => handleSessionClick()}
                        className="tw-w-full tw-text-base tw-cursor-pointer"
                    >
                        {session.name}
                    </div>
                )}
            </div>

            <div className="tw-flex tw-items-center tw-gap-x-2 tw-pr-2 tw-py-2">
                <OverlayTrigger
                    placement="bottom"
                    delay={{ show: 150, hide: 400 }}
                    overlay={<Tooltip>Rename</Tooltip>}
                >
                    <i className="ph ph-pencil-line" onClick={handleEditClick}></i>
                </OverlayTrigger>

                {!isCurrentlyWorking && sessions.length > 1 && (
                    <OverlayTrigger
                        placement="bottom"
                        delay={{ show: 150, hide: 400 }}
                        overlay={<Tooltip>Delete</Tooltip>}
                    >
                        <i
                            className="ph ph-trash"
                            onClick={() => {
                                handleDeleteSession();
                            }}
                        ></i>
                    </OverlayTrigger>
                )}
            </div>
        </div>
    );
};

export default SidebarSession;
